'use client'

import { BackdropLoading } from '@/components/backdrop-loading'
import { setupPassword, validatePasswordSetup } from '@/lib/api/auth/auth'
import { useAuth } from '@/store/hooks'
import { Link, useNavigate, useSearchParams } from 'react-router-dom'
import { useEffect, useState } from 'react'
import { MdCheckCircle, MdVisibility, MdVisibilityOff, MdError } from 'react-icons/md'
import { toast } from 'react-toastify'
import TermsAgreement from '@/components/terms-agreement/terms-agreement'

interface UserInfo {
    id: number
    name: string
    email: string
}

interface CompanyInfo {
    id: number
    name: string
    tradingName?: string
}

export default function SetupPasswordPage() {
    const [isLoading, setIsLoading] = useState(true)
    const [isSubmitting, setIsSubmitting] = useState(false)
    const [showPassword, setShowPassword] = useState(false)
    const [passwordSetup, setPasswordSetup] = useState(false)
    const [password, setPassword] = useState('')
    const [confirmPassword, setConfirmPassword] = useState('')
    const [termsAccepted, setTermsAccepted] = useState(false)
    const [tokenValid, setTokenValid] = useState(false)
    const [userInfo, setUserInfo] = useState<UserInfo | null>(null)
    const [companyInfo, setCompanyInfo] = useState<CompanyInfo | null>(null)
    const { isAuthenticated } = useAuth()
    const navigate = useNavigate()
    const params = useSearchParams()

    const token = params[0].get('token')

    useEffect(() => {
        if (isAuthenticated) {
            navigate('/')
            return
        }

        if (!token) {
            toast.error('Token invalido')
            navigate('/login')
            return
        }

        validateToken()
    }, [isAuthenticated, token])

    const validateToken = async () => {
        if (!token) return

        setIsLoading(true)
        const response = await validatePasswordSetup(token)

        if (response.success && response.result) {
            setTokenValid(true)
            setUserInfo(response.result.user)
            setCompanyInfo(response.result.company)
        } else {
            setTokenValid(false)
            toast.error(response.error || 'Token invalido ou expirado')
        }

        setIsLoading(false)
    }

    const passwordValidations = {
        length:
            password.length >= 8 &&
            /[a-zA-Z]/.test(password) &&
            /[0-9]/.test(password),
        uppercase: /[A-Z]/.test(password),
        special: /[@!#?.&$*]/.test(password),
        noSpaces: !password.includes(' '),
    }

    const allPasswordValidationsValid =
        Object.values(passwordValidations).every(Boolean)
    const passwordsMatch = password === confirmPassword
    const isFormValid =
        allPasswordValidationsValid &&
        passwordsMatch &&
        password.length > 0 &&
        confirmPassword.length > 0 &&
        termsAccepted

    const handleSubmit = async () => {
        setIsSubmitting(true)

        if (!isFormValid) {
            toast.warning('Por favor, atenda a todos os criterios de senha.')
            setIsSubmitting(false)
            return
        }

        if (!token) {
            toast.error('Token invalido')
            setIsSubmitting(false)
            return
        }

        const response = await setupPassword(token, password)

        if (response.success) {
            setPasswordSetup(true)
            toast.success('Senha criada com sucesso!')
        } else {
            toast.error(
                response.error ||
                'Ocorreu um erro ao criar a senha. Tente novamente.'
            )
        }

        setIsSubmitting(false)
    }

    const togglePasswordVisibility = () => {
        setShowPassword(!showPassword)
    }

    if (isLoading) {
        return <BackdropLoading visible={true} />
    }

    if (!tokenValid) {
        return (
            <div className="min-h-screen bg-gradient-to-br from-blue-50 to-white flex items-center justify-center p-4">
                <div className="w-full max-w-md">
                    <div className="bg-white rounded-2xl shadow-xl p-8 text-center">
                        <div className="w-16 h-16 bg-red-100 rounded-full flex items-center justify-center mx-auto mb-6">
                            <MdError className="text-red-600 text-2xl" />
                        </div>

                        <h1 className="text-2xl font-bold text-gray-900 mb-4">
                            Link Invalido ou Expirado
                        </h1>

                        <p className="text-gray-600 mb-8">
                            Este link de criacao de senha e invalido ou ja
                            expirou. Por favor, entre em contato com a empresa
                            para solicitar um novo link.
                        </p>

                        <div className="space-y-4">
                            <Link
                                to="/login"
                                className="w-full btn btn-secondary"
                            >
                                Ir para Login
                            </Link>
                        </div>
                    </div>
                </div>
            </div>
        )
    }

    if (passwordSetup) {
        return (
            <div className="min-h-screen bg-gradient-to-br from-blue-50 to-white flex items-center justify-center p-4">
                <div className="w-full !max-w-md">
                    <div className="bg-white rounded-2xl shadow-xl p-8 text-center">
                        <div className="w-16 h-16 bg-green-100 rounded-full flex items-center justify-center mx-auto mb-6">
                            <MdCheckCircle className="text-green-600 text-2xl" />
                        </div>

                        <h1 className="text-2xl font-bold text-gray-900 mb-4">
                            Senha Criada!
                        </h1>

                        <p className="text-gray-600 mb-8">
                            Sua senha foi criada com sucesso. Agora voce pode
                            fazer login com seu email e nova senha.
                        </p>

                        <div className="space-y-4">
                            <Link
                                to="/login"
                                className="w-full btn btn-secondary"
                            >
                                Fazer Login
                            </Link>
                        </div>
                    </div>
                </div>
            </div>
        )
    }

    return (
        <>
            {isSubmitting && <BackdropLoading visible={isSubmitting} />}

            <div className="min-h-screen bg-gradient-to-br from-blue-50 to-white flex items-center justify-center p-4">
                <div className="w-full !max-w-md">
                    <div className="bg-white rounded-2xl shadow-xl p-8">
                        <div className="text-center mb-8">
                            <h1 className="text-2xl font-bold text-gray-900 mb-2">
                                Criar Senha
                            </h1>
                            {companyInfo && (
                                <p className="text-sm text-brand-primary font-medium mb-2">
                                    {companyInfo.tradingName || companyInfo.name}
                                </p>
                            )}
                            {userInfo && (
                                <p className="text-gray-600">
                                    Ola {userInfo.name}, crie sua senha para
                                    acessar o sistema.
                                </p>
                            )}
                        </div>

                        <section
                            data-form-id="setup-password"
                            className="space-y-6"
                        >
                            <div>
                                <label className="block text-sm font-medium text-gray-700 mb-2">
                                    Nova Senha
                                </label>
                                <div className="relative">
                                    <input
                                        type={
                                            showPassword ? 'text' : 'password'
                                        }
                                        name="password"
                                        value={password}
                                        onChange={e =>
                                            setPassword(e.target.value)
                                        }
                                        className="input input-bordered w-full pr-12"
                                        placeholder="Digite sua senha"
                                        required
                                    />
                                    <button
                                        type="button"
                                        className="absolute inset-y-0 right-0 pr-3 flex items-center"
                                        onClick={togglePasswordVisibility}
                                    >
                                        {showPassword ? (
                                            <MdVisibilityOff className="h-5 w-5 text-gray-400" />
                                        ) : (
                                            <MdVisibility className="h-5 w-5 text-gray-400" />
                                        )}
                                    </button>
                                </div>

                                {password && (
                                    <div className="mt-3 space-y-2">
                                        <div className="flex items-center gap-2">
                                            <div
                                                className={`w-4 h-4 rounded-full flex items-center justify-center ${passwordValidations.length
                                                    ? 'bg-green-500'
                                                    : 'bg-gray-300'
                                                    }`}
                                            >
                                                {passwordValidations.length && (
                                                    <svg
                                                        className="w-2.5 h-2.5 text-white"
                                                        fill="currentColor"
                                                        viewBox="0 0 20 20"
                                                    >
                                                        <path
                                                            fillRule="evenodd"
                                                            d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z"
                                                            clipRule="evenodd"
                                                        />
                                                    </svg>
                                                )}
                                            </div>
                                            <span className="text-sm text-gray-600">
                                                A senha deve conter ao menos 8
                                                caracteres com letras e numeros
                                            </span>
                                        </div>

                                        <div className="flex items-center gap-2">
                                            <div
                                                className={`w-4 h-4 rounded-full flex items-center justify-center ${passwordValidations.uppercase
                                                    ? 'bg-green-500'
                                                    : 'bg-gray-300'
                                                    }`}
                                            >
                                                {passwordValidations.uppercase && (
                                                    <svg
                                                        className="w-2.5 h-2.5 text-white"
                                                        fill="currentColor"
                                                        viewBox="0 0 20 20"
                                                    >
                                                        <path
                                                            fillRule="evenodd"
                                                            d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z"
                                                            clipRule="evenodd"
                                                        />
                                                    </svg>
                                                )}
                                            </div>
                                            <span className="text-sm text-gray-600">
                                                Ao menos 1 letra maiuscula
                                            </span>
                                        </div>

                                        <div className="flex items-center gap-2">
                                            <div
                                                className={`w-4 h-4 rounded-full flex items-center justify-center ${passwordValidations.special
                                                    ? 'bg-green-500'
                                                    : 'bg-gray-300'
                                                    }`}
                                            >
                                                {passwordValidations.special && (
                                                    <svg
                                                        className="w-2.5 h-2.5 text-white"
                                                        fill="currentColor"
                                                        viewBox="0 0 20 20"
                                                    >
                                                        <path
                                                            fillRule="evenodd"
                                                            d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z"
                                                            clipRule="evenodd"
                                                        />
                                                    </svg>
                                                )}
                                            </div>
                                            <span className="text-sm text-gray-600">
                                                Ao menos 1 caractere especial
                                                como @! # ?. & $ *
                                            </span>
                                        </div>

                                        <div className="flex items-center gap-2">
                                            <div
                                                className={`w-4 h-4 rounded-full flex items-center justify-center ${passwordValidations.noSpaces
                                                    ? 'bg-green-500'
                                                    : 'bg-gray-300'
                                                    }`}
                                            >
                                                {passwordValidations.noSpaces && (
                                                    <svg
                                                        className="w-2.5 h-2.5 text-white"
                                                        fill="currentColor"
                                                        viewBox="0 0 20 20"
                                                    >
                                                        <path
                                                            fillRule="evenodd"
                                                            d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z"
                                                            clipRule="evenodd"
                                                        />
                                                    </svg>
                                                )}
                                            </div>
                                            <span className="text-sm text-gray-600">
                                                Nao conter espacos em branco
                                            </span>
                                        </div>
                                    </div>
                                )}
                            </div>

                            <div>
                                <label className="block text-sm font-medium text-gray-700 mb-2">
                                    Confirmar Senha
                                </label>
                                <input
                                    type={showPassword ? 'text' : 'password'}
                                    name="confirmPassword"
                                    value={confirmPassword}
                                    onChange={e =>
                                        setConfirmPassword(e.target.value)
                                    }
                                    className="input input-bordered w-full"
                                    placeholder="Confirme sua senha"
                                    required
                                />

                                {confirmPassword && (
                                    <div className="mt-3">
                                        <div className="flex items-center gap-2">
                                            <div
                                                className={`w-4 h-4 rounded-full flex items-center justify-center ${password === confirmPassword
                                                    ? 'bg-green-500'
                                                    : 'bg-red-500'
                                                    }`}
                                            >
                                                {password ===
                                                    confirmPassword ? (
                                                    <svg
                                                        className="w-2.5 h-2.5 text-white"
                                                        fill="currentColor"
                                                        viewBox="0 0 20 20"
                                                    >
                                                        <path
                                                            fillRule="evenodd"
                                                            d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z"
                                                            clipRule="evenodd"
                                                        />
                                                    </svg>
                                                ) : (
                                                    <svg
                                                        className="w-2.5 h-2.5 text-white"
                                                        fill="currentColor"
                                                        viewBox="0 0 20 20"
                                                    >
                                                        <path
                                                            fillRule="evenodd"
                                                            d="M4.293 4.293a1 1 0 011.414 0L10 8.586l4.293-4.293a1 1 0 111.414 1.414L11.414 10l4.293 4.293a1 1 0 01-1.414 1.414L10 11.414l-4.293 4.293a1 1 0 01-1.414-1.414L8.586 10 4.293 5.707a1 1 0 010-1.414z"
                                                            clipRule="evenodd"
                                                        />
                                                    </svg>
                                                )}
                                            </div>
                                            <span
                                                className={`text-sm ${password === confirmPassword
                                                    ? 'text-green-600'
                                                    : 'text-red-600'
                                                    }`}
                                            >
                                                {password === confirmPassword
                                                    ? 'As senhas coincidem'
                                                    : 'As senhas nao coincidem'}
                                            </span>
                                        </div>
                                    </div>
                                )}
                            </div>

                            <TermsAgreement
                                checked={termsAccepted}
                                onChange={setTermsAccepted}
                            />

                            <button
                                type="button"
                                className={`w-full btn btn-secondary ${!isFormValid
                                    ? 'opacity-50 cursor-not-allowed'
                                    : ''
                                    }`}
                                disabled={isSubmitting || !isFormValid}
                                onClick={handleSubmit}
                            >
                                {isSubmitting ? (
                                    <span className="loading loading-spinner loading-sm"></span>
                                ) : (
                                    'Criar Senha'
                                )}
                            </button>
                        </section>

                        <div className="mt-6 text-center">
                            <Link
                                to="/login"
                                className="text-sm text-brand-primary hover:text-brand-primary/80 font-medium"
                            >
                                Ja tenho uma conta
                            </Link>
                        </div>
                    </div>
                </div>
            </div>
        </>
    )
}
